import { formRegex, tLabel } from '@/core'
import { tMessage } from '@/core/form'

/**
 * fullName validation function
 * Validates that the fullName:
 * 1. Has a maximum length of 50 characters
 * 2. Does not contain special characters
 *
 * @param o - The validation object (from schema definition)
 * @param labelKey - The translation key for the label
 * @returns The validation chain
 */
export const validateFullName = (o: any, labelKey: string) => {
  return o
    .string()
    .max(50)
    .custom((value: string, helpers: any) => {
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * fullName validation function with conditional validation
 * Used when validation depends on another field's value
 *
 * @param o - The validation object (from schema definition)
 * @param condition - The condition object with field, value, and labelKey
 * @returns The validation chain
 */
export const validateFullNameConditional = (
  o: any,
  condition: { field: string; value: any; labelKey: string }
) => {
  return o.string().when(condition.field, {
    is: condition.value,
    then: validateFullName(o, condition.labelKey),
    otherwise: o
      .string()
      .max(50)
      .optional()
      .allow('')
      .custom((value: string, helpers: any) => {
        const match = value.match(/[=+\-@\t\r\n;,]+/)
        if (match) {
          return helpers.message({
            custom: tMessage('common:inputContainSpecialChars', {
              label: tLabel(condition.labelKey),
            }),
          })
        }
        return value
      })
      .label(tLabel(condition.labelKey)),
  })
}

/**
 * prefecture validation function
 * Validates that the prefecture:
 * 1. Has a maximum length of 150 characters
 * 2. Does not contain special characters
 *
 * @param o - The validation object (from schema definition)
 * @param labelKey - The translation key for the label
 * @returns The validation chain
 */
export const validatePrefecture = (o: any, labelKey: string) => {
  return o
    .string()
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * city validation function
 * Validates that the city:
 * 1. Has a maximum length of 150 characters
 * 2. Does not contain special characters
 *
 * @param o - The validation object (from schema definition)
 * @param labelKey - The translation key for the label
 * @returns The validation chain
 */
export const validateCity = (o: any, labelKey: string) => {
  return o
    .string()
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * line1 validation function
 * Validates that the line1:
 * 1. Has a maximum length of 150 characters
 * 2. Does not start with a hyphen
 * 3. Does not contain special characters (excluding hyphen)
 *
 * @param o - The validation object (from schema definition)
 * @param labelKey - The translation key for the label
 * @returns The validation chain
 */
export const validateLine1 = (o: any, labelKey: string) => {
  return o
    .string()
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value.match(/^-.*$/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputStartsWithSymbol', {
            symbol: match[0].slice(0, 1),
            label: tLabel(labelKey),
          }),
        })
      }
      const specialCharsRegex = value.match(/[=+@\t\r\n;,]+/)
      if (specialCharsRegex) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * line2 validation function
 * Validates that the line2:
 * 1. Has a maximum length of 150 characters
 * 2. Does not contain special characters
 * 3. Is optional
 *
 * @param o - The validation object (from schema definition)
 * @param labelKey - The translation key for the label
 * @returns The validation chain
 */
export const validateLine2 = (o: any, labelKey: string) => {
  return o
    .string()
    .optional()
    .allow('')
    .max(150)
    .custom((value: string, helpers: any) => {
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * email validation function
 * Validates that the email:
 * 1. Is a valid email format
 * 2. Does not start with special characters (=, +, -, @)
 * 3. Does not contain special characters (=, ;, tabs, newlines, carriage returns, commas)
 *
 * @param o - The validation object (from schema definition)
 * @param labelKey - The translation key for the label
 * @returns The validation chain
 */
export const validateEmail = (o: any, labelKey: string) => {
  return o
    .string()
    .regex(formRegex.email)
    .custom((value: string, helpers: any) => {
      const match = value.match(/^([=+\-@])/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputStartsWithSymbol', {
            symbol: match[1],
            label: tLabel(labelKey),
          }),
        })
      }
      const specialCharsRegex = /[=;\t\n\r,]/
      const specialCharsMatch = value.match(specialCharsRegex)
      if (specialCharsMatch) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}

/**
 * displayName validation function
 * Validates that the displayName:
 * 1. Has a maximum length of 50 characters
 * 2. Does not contain special characters
 *
 * @param o - The validation object (from schema definition)
 * @param labelKey - The translation key for the label
 * @returns The validation chain
 */
export const validateDisplayName = (o: any, labelKey: string) => {
  return o
    .string()
    .max(50)
    .custom((value: string, helpers: any) => {
      const match = value.match(/[=+\-@\t\r\n;,]+/)
      if (match) {
        return helpers.message({
          custom: tMessage('common:inputContainSpecialChars', {
            label: tLabel(labelKey),
          }),
        })
      }
      return value
    })
    .label(tLabel(labelKey))
}
